/**
 * VirtualizedDayItem - Represents a single virtualized day in the calendar
 * Renders efficiently as part of react-window's virtualized list
 */

import React, { memo, useMemo } from 'react';
import { format, getDate, getDay, isSameDay, isWeekend } from 'date-fns';
import { useDrop } from 'react-dnd';
import { DayShift, Employee, HourRange, BusinessClosedDate, DayTemplate } from '@/types';
import { Holiday } from '@/lib/scheduling/holidays-simple';
import { ShiftRow } from '@/lib/scheduling/shift-utils';
import { ROW_HEIGHT_PX, INTERVALS_PER_HOUR, INTERVAL_MINUTES, MINUTES_PER_HOUR } from '@/components/shifts/constants';
import { getPreparationStatusClass } from '@/lib/scheduling/calculate-preparation-from-context';
import { Z_INDEX_LAYERS } from '@/lib/scheduling/z-index-layers';
import { EDITING_COLORS } from '@/lib/scheduling/editing-colors';
import DayRow from '@/components/shifts/DayRow';
import GridDropOverlay from '@/components/shifts/GridDropOverlay';
import VirtualizedShiftLayer from './VirtualizedShiftLayer';
import ShiftCreationOverlay from '@/components/shifts/ShiftCreationOverlay';
import { Calendar } from 'lucide-react';

interface DayRowCalculation {
  dayDate: Date;
  dayOfMonth: number;
  dailyShifts: DayShift[];
  organizedShiftRows: ShiftRow[];
  numRows: number;
  hasDroppedTemplate: boolean;
  hasShifts: boolean;
}

interface VirtualizedDayItemProps {
  index: number;
  style: React.CSSProperties;
  dayDate: Date;
  dayCalc: DayRowCalculation;

  // Shared data passed via react-window's itemData
  itemData: {
    // Day offsets
    dayRowOffsets: { [key: number]: number };

    // Shift data
    shifts: DayShift[];
    shiftsByDay: { [key: number]: DayShift[] };
    shiftsByDayOrganized: { [key: number]: ShiftRow[] };

    // Employee data
    employees: Employee[];

    // Display configuration
    totalDisplayHours: number;
    effectiveDisplayStartTime: Date;
    effectiveDisplayEndTime: Date;
    hourCellWidthPx: number;
    gridTemplateRows: string;

    // Business context
    businessId?: string | null;
    countryCode?: string;
    openingHoursByDayOfWeek: Map<number, any>;
    closedDatesMap?: Map<string, BusinessClosedDate>;
    workingHours: HourRange[];

    // UI state
    hideDayColumn?: boolean;
    isEditingMode?: boolean;
    show24Hours?: boolean;
    expandedDayKeys: string[];
    selectedEditingDate?: Date | null;

    // Event handlers
    onDaySelect?: (date: Date) => void;
    onAssignEmployee?: (shiftId: string, employeeId: string | null) => void;
    onCreateShift?: (businessId: string, startTime: Date, endTime: Date, title: string) => Promise<void>;
    onRepositionShift?: (shiftId: string, newStartTime: Date, newEndTime: Date) => Promise<void>;
    onOptimisticPositionUpdate?: (shiftId: string, newStartTime: Date, newEndTime: Date) => void;
    onOptimisticPositionClear?: (shiftId: string) => void;
    onShiftSelect?: (shiftId: string, selected: boolean) => void;
    onDeleteShift?: (shiftId: string) => void;
    selectedShiftIds?: Set<string>;
    actionsPanelMode?: string;
    onOpenActionsPanel?: () => void;
    onSetActionsPanelMode?: (mode: string) => void;
    isActionsPanelOpen?: boolean;

    // Template handlers
    onTemplateCreationClick?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
    onTemplateCreationHover?: (cellTime: Date, rowIndex?: number, dayIndex?: number) => void;
    onOpenInlineBuilder?: (cellTime: Date, event: React.MouseEvent, rowIndex?: number, dayIndex?: number) => void;
    isTemplateCreationInProgress?: boolean;
    // Day template drop handler
    onDayTemplateDrop?: (template: DayTemplate, targetDate: Date) => void;

    // Shift creation overlay state
    templateCreationStartTime?: Date | null;
    templateCreationEndTime?: Date | null;
    templateCreationCursorTime?: Date | null;
    templateCreationDayIndex?: number | null;
    templateCreationRowIndex?: number | null;
    isResizeMode?: boolean;
    onTemplateResizeComplete?: (startTime: Date, endTime: Date) => void;
    onTemplateFinalizedClick?: () => void;

    // Holiday check function
    checkIsHoliday?: (date: Date) => Holiday | null;

    // Time functions - simplified to avoid callback instability
    currentTimeHours?: number;
    currentTimeMinutes?: number;
    getDisplayTimesForDay?: (dayDate: Date, openingHoursByDayOfWeek: Map<number, any>) => {
      startTime: Date;
      endTime: Date;
      totalHours: number;
    };

    // Day validation status
    getDayStatusByKey?: (dateKey: string) => { status: string; [key: string]: any } | null;

    scrollContainerRef?: React.RefObject<HTMLDivElement>;

    // Hide status indicators (day borders)
    hideStatusIndicators?: boolean;
  };
}

const VirtualizedDayItem: React.FC<VirtualizedDayItemProps> = memo(
  ({
    index,
    style,
    dayDate,
    dayCalc,
    itemData,
  }) => {
    // Destructure all shared props from itemData
    const {
      dayRowOffsets,
      shiftsByDayOrganized,
      hideDayColumn = false,
      openingHoursByDayOfWeek,
      closedDatesMap,
      checkIsHoliday,
      selectedEditingDate,
      countryCode,
      getDayStatusByKey,
      gridTemplateColumns,
      ...props
    } = itemData;

    const dayOfMonth = dayCalc.dayOfMonth;
    const isToday = isSameDay(dayDate, new Date());
    const isWeekendDay = isWeekend(dayDate);
    const dayKey = format(dayDate, 'yyyy-MM-dd');
    const holiday = countryCode && checkIsHoliday ? checkIsHoliday(dayDate) : null;
    const isCurrentlyEditing = selectedEditingDate ? isSameDay(dayDate, selectedEditingDate) : false;

    // Get validation status for this day
    const dayValidationStatus = getDayStatusByKey ? getDayStatusByKey(dayKey) : null;

    // Hide status indicators if requested (e.g., for view-only calendars)
    const hideStatusIndicators = itemData.hideStatusIndicators ?? false;

    // Provide fallback border styling when preparation status is loading
    // This ensures borders are always visible, even during initial load
    const statusBorderClass = hideStatusIndicators
      ? '' // No status border for view-only calendars
      : dayValidationStatus
        ? getPreparationStatusClass(dayValidationStatus.status, 'border')
        : 'border-l-2 border-l-gray-300 dark:border-l-gray-600'; // Loading state border

    // Get organized shifts for this day
    const organizedShifts = shiftsByDayOrganized[dayOfMonth] || [];

    // Check for custom closed date (highest precedence)
    const customClosedDate = closedDatesMap?.get(dayKey);
    const isCustomClosed = customClosedDate !== undefined;

    // Calculate opening hours status (weekly schedule)
    const dayOfWeekNum = getDay(dayDate);
    const openingHoursForDay = openingHoursByDayOfWeek?.get(dayOfWeekNum);
    const hasOpeningHoursData = openingHoursByDayOfWeek?.size > 0;
    const isWeeklyScheduleClosed = hasOpeningHoursData && (!openingHoursForDay?.is_open || openingHoursForDay?.is_closed_all_day === true);

    // Day is closed if either custom closed OR weekly schedule closed
    const isDayClosedAllDay = isCustomClosed || isWeeklyScheduleClosed;

    // Day template drop zone - accepts DAY_TEMPLATE drag type
    const [{ isOver: isDayTemplateOver, canDrop: canDropDayTemplate, isDayTemplateDragging }, dayTemplateDrop] = useDrop(() => ({
      accept: 'DAY_TEMPLATE',
      drop: (item: { templateId: string; template: DayTemplate }) => {
        if (props.onDayTemplateDrop) {
          props.onDayTemplateDrop(item.template, dayDate);
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        canDrop: !!monitor.canDrop(),
        // Check if a day template is being dragged anywhere
        isDayTemplateDragging: monitor.getItemType() === 'DAY_TEMPLATE',
      }),
    }), [dayDate, props.onDayTemplateDrop]);

    // Day status classes - custom closed dates get pink/rose styling, weekly closed get gray
    const getDayStatusClasses = () => {
      if (isCurrentlyEditing) {
        return `${EDITING_COLORS.BG_SOLID} ${EDITING_COLORS.TEXT_ON_DARK}`;
      }
      if (isToday) {
        return 'bg-orange-500 text-white';
      }
      // Custom closed dates get pink/rose styling to distinguish from weekly schedule and holidays
      if (isCustomClosed) {
        return 'bg-pink-500 dark:bg-pink-600 text-white';
      }
      // Weekly schedule closed dates get gray styling
      if (isWeeklyScheduleClosed) {
        return 'bg-gray-400 dark:bg-gray-500 text-white';
      }
      if (holiday && holiday.is_non_working_day) {
        return 'bg-destructive text-destructive-foreground';
      }
      if (isWeekendDay) {
        return 'bg-primary text-primary-foreground';
      }
      return 'bg-background text-foreground';
    };

    // Calculate CSS Grid template for this day (rows only - columns come from itemData)
    const gridTemplateRowsForDay = `repeat(${dayCalc.numRows}, ${ROW_HEIGHT_PX}px)`;

    // Calculate current time line position locally to avoid callback instability
    const getCurrentTimePosition = (): string => {
      if (!props.hourCellWidthPx || props.hourCellWidthPx <= 0 || !props.effectiveDisplayStartTime || props.totalDisplayHours <= 0) {
        return '0px';
      }

      if (props.currentTimeHours === undefined || props.currentTimeMinutes === undefined) {
        return '0px';
      }

      const displayStartMinutes = props.effectiveDisplayStartTime.getHours() * 60 + props.effectiveDisplayStartTime.getMinutes();
      const totalDisplayMinutes = props.totalDisplayHours * 60;
      const currentTimeMinutes = props.currentTimeHours * 60 + props.currentTimeMinutes;

      let relativeCurrentTimeMinutes = currentTimeMinutes - displayStartMinutes;

      // Handle overnight shifts
      if (relativeCurrentTimeMinutes < 0 && props.totalDisplayHours > 24) {
        relativeCurrentTimeMinutes += 24 * 60;
      }

      const clampedCurrentTimeMinutes = Math.max(0, Math.min(totalDisplayMinutes, relativeCurrentTimeMinutes));

      // Calculate pixel position
      const totalIntervals = Math.ceil(props.totalDisplayHours * INTERVALS_PER_HOUR);
      const timeIntervalsWidth = totalIntervals * props.hourCellWidthPx;
      const timePositionPixels = (clampedCurrentTimeMinutes / totalDisplayMinutes) * timeIntervalsWidth;

      // Day column offset removed - CSS Grid handles column spacing
      return `${timePositionPixels}px`;
    };

    return (
      <div
        style={{
          // Use react-window's positioning for vertical stacking
          position: style.position || 'absolute',
          top: style.top,
          left: style.left || 0,
          height: style.height,
          width: style.width || '100%',
          // Apply grid layout
          display: 'grid',
          gridTemplateColumns: gridTemplateColumns,
          gridTemplateRows: gridTemplateRowsForDay,
          rowGap: 0,
          columnGap: 0,
        }}
        className="virtualized-day-item shift-calendar-grid"
        data-calendar-grid
      >
        {/* Day Label Column */}
        {!hideDayColumn && (
          <div
            className={`flex flex-col items-center justify-center cursor-pointer select-none sticky left-0 z-10 ${
              isCurrentlyEditing
                ? `border-2 ${EDITING_COLORS.BORDER_SOLID}`
                : 'border-t border-b border-t-border border-b-border'
            } ${statusBorderClass} ${getDayStatusClasses()}`}
            data-day-column
            style={{
              gridColumn: 1,
              gridRow: `1 / ${dayCalc.numRows + 1}`, // Span all rows
              width: '32px',
              minWidth: '32px',
              height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
            }}
            onClick={() => props.onDaySelect?.(dayDate)}
            title={holiday ? holiday.name : undefined}
          >
            <div className="text-xs opacity-75 font-normal">
              {format(dayDate, 'EEE')}
            </div>
            <div className="text-base font-semibold">
              {dayOfMonth}
            </div>
            {holiday && (
              <div className="text-xs mt-1 text-center opacity-90">
                <Calendar className="h-3 w-3" />
              </div>
            )}
            {hasOpeningHoursData && openingHoursForDay?.is_24_hours && !holiday && (
              <div className="text-xs mt-1 text-center opacity-90 font-bold">
                24
              </div>
            )}
            {isCurrentlyEditing && (
              <div className="text-xs mt-1 opacity-90">Edit</div>
            )}
          </div>
        )}

        {/* Day Rows */}
        {Array.from({ length: dayCalc.numRows }, (_, rowIndex) => {
          const gridRowIndex = rowIndex + 1; // Local 1-based index for this day's grid
          const isFirstRowInDay = rowIndex === 0;
          const isLastRowInDay = rowIndex === dayCalc.numRows - 1;

          return (
            <DayRow
              key={`day-row-${dayKey}-${rowIndex}`}
              dayDate={dayDate}
              dayIndex={index}
              dayOfMonth={dayOfMonth}
              dayKey={dayKey}
              rowIndex={rowIndex}
              gridRowIndex={gridRowIndex}
              isToday={isToday}
              isWeekendDay={isWeekendDay}
              holiday={holiday}
              isCurrentlyEditing={isCurrentlyEditing}
              openingHoursByDayOfWeek={openingHoursByDayOfWeek}
              customClosedDate={customClosedDate}
              isFirstRowInDay={isFirstRowInDay}
              isLastRowInDay={isLastRowInDay}
              {...props}
            />
          );
        })}

        {/* GridDropOverlay - Single drop zone per day for all interactions */}
        {/* Only render when hourCellWidthPx is valid to prevent misaligned positioning */}
        {props.hourCellWidthPx > 0 && (
          <GridDropOverlay
            dayDate={dayDate}
            dayIndex={index}
            effectiveDisplayStartTime={props.effectiveDisplayStartTime}
            totalDisplayHours={props.totalDisplayHours}
            hourCellWidthPx={props.hourCellWidthPx}
            rowHeightPx={ROW_HEIGHT_PX}
            numRows={dayCalc.numRows}
            businessId={props.businessId || null}
            gridRowStart={1}
            gridRowEnd={dayCalc.numRows + 1}
            gridColumnEnd={Math.ceil((isNaN(props.totalDisplayHours) ? 24 : props.totalDisplayHours) * INTERVALS_PER_HOUR) + (hideDayColumn ? 1 : 2)}
            hideDayColumn={hideDayColumn}
            onCreateShift={props.onCreateShift}
            onRepositionShift={props.onRepositionShift}
            onOptimisticPositionUpdate={props.onOptimisticPositionUpdate}
            onOptimisticPositionClear={props.onOptimisticPositionClear}
            onTemplateCreationClick={props.onTemplateCreationClick}
            onOpenInlineBuilder={props.onOpenInlineBuilder}
            isTemplateCreationInProgress={props.isTemplateCreationInProgress || false}
          />
        )}

        {/* Day Template Drop Zone - Spans entire day, only active when day template is being dragged */}
        <div
          ref={dayTemplateDrop}
          className={`${isDayTemplateDragging && isDayTemplateOver && canDropDayTemplate ? `${EDITING_COLORS.BG_LIGHT} border-2 ${EDITING_COLORS.BORDER_SOLID}` : ''}`}
          style={{
            gridRow: `1 / ${dayCalc.numRows + 1}`,
            gridColumn: '1 / -1',
            height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
            zIndex: isDayTemplateDragging ? Z_INDEX_LAYERS.DROP_ZONE : 1,
            pointerEvents: isDayTemplateDragging ? 'auto' : 'none',
          }}
        />

        {/* Virtualized Shift Layer */}
        <VirtualizedShiftLayer
          dayDate={dayDate}
          dayKey={dayKey}
          organizedShifts={organizedShifts}
          numRows={dayCalc.numRows}
          isToday={isToday}
          {...props}
        />

        {/* Closed Day Badge - Different styling for custom closed dates vs weekly schedule */}
        {isDayClosedAllDay && dayCalc.dailyShifts.length === 0 && (
          <div
            className="absolute top-0 left-0 right-0 pointer-events-none flex items-center justify-center"
            style={{
              height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
              zIndex: 7,
            }}
          >
            <div
              className={`px-2 py-1 rounded text-xs font-medium ${
                isCustomClosed
                  ? 'bg-pink-600/80 dark:bg-pink-500/80 text-white'
                  : 'bg-gray-600/80 dark:bg-gray-400/80 text-white dark:text-gray-900'
              }`}
              title={customClosedDate?.reason || undefined}
            >
              {isCustomClosed ? 'CLOSED MANUALLY' : 'CLOSED'}
            </div>
          </div>
        )}

        {/* Custom Closed Date indicator (shown even when there are shifts) */}
        {isCustomClosed && dayCalc.dailyShifts.length > 0 && (
          <div
            className="absolute top-1 right-1 z-10 pointer-events-none"
            title={customClosedDate?.reason || 'Closed manually'}
          >
            <div className="bg-pink-600/90 text-white px-1.5 py-0.5 rounded text-xs font-medium shadow-sm">
              CLOSED MANUALLY
            </div>
          </div>
        )}

        {/* Current Time Indicator */}
        {isToday && (
          <div
            className="absolute bg-red-500 w-0.5 pointer-events-none current-time-line"
            style={{
              left: getCurrentTimePosition(),
              top: 0,
              height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
              zIndex: 5,
            }}
          />
        )}

        {/* Shift Creation Overlay - render if this specific day is being edited */}
        {props.templateCreationDayIndex === index &&
         props.templateCreationStartTime && (() => {
          // **CURSOR FOLLOW FIX**: Use cursor time if no end time is set yet (preview mode)
          const effectiveEndTime = props.templateCreationEndTime || props.templateCreationCursorTime || props.templateCreationStartTime;

          // **INTERACTION FIX**: Validate coordinates before rendering
          console.log(`🎯 Rendering ShiftCreationOverlay for day ${index}:`, {
            dayDate: format(dayDate, 'yyyy-MM-dd'),
            startTime: format(props.templateCreationStartTime, 'HH:mm'),
            endTime: format(effectiveEndTime, 'HH:mm'),
            hasEndTime: !!props.templateCreationEndTime,
            hasCursorTime: !!props.templateCreationCursorTime,
            cursorTime: props.templateCreationCursorTime ? format(props.templateCreationCursorTime, 'HH:mm') : 'none',
            targetRowIndex: props.templateCreationRowIndex || 0,
            numRows: dayCalc.numRows,
            isResizeMode: props.isResizeMode,
            hourCellWidthPx: props.hourCellWidthPx,
            hasHoverHandler: !!props.onTemplateCreationHover
          });

          return (
            <ShiftCreationOverlay
              startTime={props.templateCreationStartTime}
              endTime={effectiveEndTime}
              effectiveDisplayStartTime={props.effectiveDisplayStartTime}
              effectiveDisplayEndTime={props.effectiveDisplayEndTime}
              totalDisplayHours={props.totalDisplayHours}
              calculatedHourCellWidthPx={props.hourCellWidthPx}
              rowHeightPx={ROW_HEIGHT_PX}
              intervalMinutes={INTERVAL_MINUTES}
              minutesPerHour={MINUTES_PER_HOUR}
              targetRowIndex={Math.min(props.templateCreationRowIndex || 0, Math.max(0, dayCalc.numRows - 1))}
              isResizeMode={props.isResizeMode || false}
              onResizeComplete={props.onTemplateResizeComplete}
              onFinalizedClick={props.onTemplateFinalizedClick}
            />
          );
        })()}

        {/* Day Selection Overlay - show border around entire day when selected for editing */}
        {isCurrentlyEditing && (
          <div
            className={`absolute inset-0 border-2 ${EDITING_COLORS.BORDER_SOLID} ${EDITING_COLORS.BG_SUBTLE} pointer-events-none`}
            style={{
              zIndex: 5, // Above hour cells but below shifts and other overlays
              left: hideDayColumn ? 0 : 0, // Start from left edge
              top: 0,
              width: '100%',
              height: `${dayCalc.numRows * ROW_HEIGHT_PX}px`,
            }}
            data-day-selection-overlay
          />
        )}
      </div>
    );
  },
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    // CRITICAL: Re-render if style changes (react-window positioning)
    // This is essential for proper virtualization - when items above change size,
    // react-window updates style.top to reposition items below
    if (
      prevProps.style.top !== nextProps.style.top ||
      prevProps.style.height !== nextProps.style.height
    ) {
      return false;
    }

    // Re-render if core data changes
    if (
      prevProps.index !== nextProps.index ||
      prevProps.dayDate.getTime() !== nextProps.dayDate.getTime() ||
      prevProps.dayCalc.numRows !== nextProps.dayCalc.numRows ||
      prevProps.dayCalc.hasShifts !== nextProps.dayCalc.hasShifts
    ) {
      return false;
    }

    // Re-render if shifts change (reference equality)
    if (prevProps.itemData.shifts !== nextProps.itemData.shifts) {
      return false;
    }

    // Re-render if closed dates map changes (reference equality)
    if (prevProps.itemData.closedDatesMap !== nextProps.itemData.closedDatesMap) {
      return false;
    }

    // Re-render if selected date changes
    if (prevProps.itemData.selectedEditingDate?.getTime() !== nextProps.itemData.selectedEditingDate?.getTime()) {
      return false;
    }

    // Re-render if action panel mode changes
    if (prevProps.itemData.actionsPanelMode !== nextProps.itemData.actionsPanelMode) {
      return false;
    }

    // Re-render if selection state changes
    if (prevProps.itemData.selectedShiftIds !== nextProps.itemData.selectedShiftIds) {
      return false;
    }

    // Re-render if historical edit mode changes (for HourCell protection state)
    if (prevProps.itemData.isHistoricalEditEnabled !== nextProps.itemData.isHistoricalEditEnabled) {
      return false;
    }

    // Re-render if editing mode changes (for inline shift creation)
    if (prevProps.itemData.isEditingMode !== nextProps.itemData.isEditingMode) {
      return false;
    }

    // **CURSOR FOLLOW FIX**: ALWAYS re-render if this is the active template creation day
    // This ensures cursor movements trigger immediate updates
    if (nextProps.itemData.templateCreationDayIndex === nextProps.index) {
      console.log('🔄 VirtualizedDayItem FORCE re-render - active template day:', {
        dayIndex: nextProps.index,
        cursorTime: nextProps.itemData.templateCreationCursorTime ? format(nextProps.itemData.templateCreationCursorTime, 'HH:mm') : 'none',
        updateKey: (nextProps.itemData as any)._cursorUpdateKey
      });
      return false; // Always re-render the active template creation day
    }

    // If this WAS the template creation day but isn't anymore, re-render to clear overlay
    if (prevProps.itemData.templateCreationDayIndex === prevProps.index &&
        nextProps.itemData.templateCreationDayIndex !== nextProps.index) {
      console.log('🔄 VirtualizedDayItem re-render - clearing template overlay');
      return false;
    }

    // Don't re-render for other prop changes
    return true;
  }
);

VirtualizedDayItem.displayName = 'VirtualizedDayItem';

export default VirtualizedDayItem;